import React, { useMemo } from 'react';
import { CreativePerformance } from './Results/DetailData/CreativePerformance';
import { useTable, useSortBy } from 'react-table';

import '../styles.scss';
import { toCurrency } from '../../../utils/numbers';
import { SummaryTable } from './Results/SummaryTable/SummaryTable';
import { restoreData } from '../../StaticReport';
import { useSummaryCols } from './Results/SummaryTable/columns';
import GroupByProvider from '../contexts/GroupByContext';
import { useVisualsData } from './Results/DetailData/hooks';
import { Section } from './shared/Helpers';
import { ErrorBoundary } from '../../../components/Helpers/Error';
import { Insights } from '../../CreativeAnalytics/PivotReport/Insights';

const deriveData = (data = {}) => {
  return { ...data, tableData: restoreData(data.tableData) };
};
export const StaticLabelPerformance = ({
  data,
  visual_id_data,
  reportBuilderGroupBy,
}) => {
  const { reportSection, sections, insightsData } = deriveData(data);
  const summaryCols = useSummaryCols(
    data.isROAS,
    data.summary_data || data.tableData,
    data.master_mappings
  );

  if (!sections) {
    return <p>There was an error.</p>;
  }

  console.log({ data, visual_id_data, summaryCols });

  return (
    <GroupByProvider existing={reportBuilderGroupBy}>
      <div className="d-flex justify-content-between row">
        <Layout>
          {sections.summary_data && (
            <Section title="Summary Data">
              <StaticSummaryWrapper
                isROAS={data.isROAS}
                tableData={data.tableData}
                selectedColumns={data.columns}
              />
            </Section>
          )}

          {sections.detail_data && (
            <Section title="Detail Data">
              <CreativePerformance
                data={data.visuals}
                selectedSummaryColumns={data.columns}
                columnOptions={data.columnOptions}
                sort={data.sort}
                master_mappings={data.master_mappings}
                summaryCols={summaryCols}
                visual_id_data={visual_id_data}
              />
            </Section>
          )}
        </Layout>

        {sections.analytics_intelligence && (
          <div className="col-lg-4 darwin-iq-right-rail">
            <ErrorBoundary>
              <Insights insightsData={insightsData} allowDataMode />
            </ErrorBoundary>
          </div>
        )}
      </div>
    </GroupByProvider>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="p-4 col-lg-8" style={{ height: '80vh' }}>
      {children}
    </div>
  );
};

const baseSummaryCols = [
  { Header: 'Ad Name Group', accessor: 'ad_name_group' },
  { Header: 'Label Name', accessor: 'label_name' },
  {
    Header: 'Total Spend',
    accessor: 'total_spend',
    Cell: ({ value }) => toCurrency(value),
  },
];

const roasSummaryCols = [
  {
    Header: 'ROAS',
    accessor: 'total_roas',
    Cell: ({ value }) => value?.toFixed(2) || value || '-',
  },
  {
    Header: 'Total Revenue',
    accessor: 'total_revenue',
    Cell: ({ value }) => toCurrency(value),
  },
];

const cpaSummaryCols = [
  { Header: 'Total Link Clicks', accessor: 'total_link_clicks' },
  { Header: 'Total Results', accessor: 'total_results' },

  {
    Header: 'Total CPA',
    accessor: 'total_cpa',
    Cell: ({ value }) => toCurrency(value),
  },
  {
    Header: 'Total CPC',
    accessor: 'total_cpc',
    Cell: ({ value }) => toCurrency(value),
  },
  {
    Header: 'CVR',
    accessor: 'CVR',
  },
];

const StaticSummaryWrapper = ({ isROAS, tableData, selectedColumns }) => {
  const { data } = tableData;

  const defaultColumn = useMemo(
    () => ({
      minWidth: 30,
      width: 150,
      maxWidth: 200,
    }),
    []
  );

  const columns = useMemo(() => {
    if (isROAS) {
      return [...baseSummaryCols, ...roasSummaryCols];
    }

    return [...baseSummaryCols, ...cpaSummaryCols];
  }, []);

  const visibleColumns = useMemo(
    () =>
      !selectedColumns?.length
        ? columns
        : columns.filter((col) =>
            selectedColumns.includes(col.metricName || col.accessor)
          ),
    [columns, selectedColumns]
  );

  const tableInstance = useTable(
    { columns: visibleColumns, data: data ?? tableData, defaultColumn },
    useSortBy
  );

  return <SummaryTable tableInstance={tableInstance} />;
};
